/* eslint-disable no-unused-vars */
import { Filter as FilterIcon, Plus, Search } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { debounce } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import Filter from './filter/Filter';
import { SearchContext } from '../features/project/building/Building';
import BaseContainer from './containers/BaseContainer';
import Bar from './elements/Bar';
import NavListCustomInfinite from './elements/NavListCustomInfinite';
import { useGetNavigation, type NavigationOptions } from '../hooks/useNavigation';
import useWindowSize from '../hooks/useWindowSize';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { useTypemarkContext } from '../features/project/typemark/TypemarkContext';

export default function NLeftPanel({
  makeActive,
  active,
  addItem,
  title,
  filteredValues,
  disableAdd = false,
  classification = undefined,
  permission = 2,
  queryKey,
  navigationOptions,
}: {
  makeActive?: any;
  active?: any;
  addItem?: any;
  title?: any;
  filteredValues?: any;
  disableAdd?: boolean;
  classification?: any;
  permission?: number;
  queryKey?: any;
  navigationOptions?: NavigationOptions;
  onSearch?: any;
  onFilter?: any;
  defaultFilterValues?: any;
  submit?: any;
}) {
  const [, windowHeight] = useWindowSize();

  const context = useTypemarkContext() || useContext(SearchContext);

  const { search, filter, setSearch, selected = {}, setSelected } = context || {};

  useQueryClient();
  const { fetchNextPage, hasNextPage, isFetching, isLoading, navigationItems } = useGetNavigation(
    queryKey,
    search,
    filter,
    classification,
    navigationOptions
  );

  useEffect(() => {
    if (
      !active &&
      navigationItems.length > 0 &&
      !isLoading &&
      !isFetching &&
      !Object.values(selected).length
    ) {
      makeActive((navigationItems[0] as any)?.id);
      setSelected?.({ [(navigationItems[0] as any)?.id]: (navigationItems[0] as any)?.id });
    }
  }, [active, navigationItems, isLoading, isFetching, hasNextPage]);

  const handleDebouncedSearch = debounce((e) => {
    setSearch(e.target.value);
  }, 400);

  const prepareData = () =>
    (navigationItems || []).map((item: any) => ({ ...(item as any), text: item?.title }));

  const handleUncheck = () => {
    setSelected?.({});
  };

  const content = () => {
    const items = [
      ...(Object.keys(selected).length > 1
        ? [
            <Tooltip key={1} side="top" content="Zrušit vybrané">
              <Dismiss12Regular
                data-testid="n-left-panel-uncheck-btn"
                onClick={() => handleUncheck()}
              />
            </Tooltip>,
          ]
        : []),
      <Filter key={2} filteredValues={filteredValues}>
        <FilterIcon className="h-4 w-4" data-testid="n-left-panel-filter-btn" />
      </Filter>,
    ];
    if (permission === 2 && !disableAdd) {
      items.push(
        <Plus key={3} className="h-4 w-4" data-testid="n-left-panel-add-btn" onClick={addItem} />
      );
    }

    return items;
  };

  return (
    <BaseContainer width="340px" className="border-r border-neutral-150">
      <Bar text={title} count={navigationItems?.length} content={content()} />

      {/* Search strip */}
      <div className="p-3 border-b border-neutral-150 bg-neutral-25/50">
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"
          />
          <input
            placeholder="Vyhledat..."
            data-testid="n-left-panel-search-input"
            onChange={handleDebouncedSearch}
            className="h-8 w-full bg-white border border-neutral-200 rounded pl-8 pr-3 text-xs placeholder:text-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
          />
        </div>
      </div>

      {/* List */}
      <div style={{ height: 'calc(100vh - 180px)' }} className="overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <SpinnerOverlay />
          </div>
        ) : (
          <NavListCustomInfinite
            height={(Number(windowHeight) || 500) - 180}
            data={prepareData()}
            makeActive={makeActive}
            active={active}
            hasMore={hasNextPage}
            dataLength={null}
            fetchMoreData={fetchNextPage}
            loading={isLoading}
            isFetching={isFetching}
          />
        )}
      </div>
    </BaseContainer>
  );
}
